/**
 * Supported language codes per application instance country.
 * The first entry of each list is the instance's default (main) language,
 * used as a fallback when the requested language is not supported.
 */
export const COUNTRY_LANGUAGES: Record<string, string[]> = {
    AT: ['de'],
    BE: ['nl', 'fr'],
    CH: ['de', 'fr'],
    CZ: ['cs'],

    // CAHO-607
    // Only ['de', 'en'] are needed for the DE instance, but FR, NL, PL, SK and ES
    // are also enabled for testing purposes (INSTANCE_COUNTRY_CODE is hardcoded to 'DE' now).
    DE: ['de', 'en', 'fr', 'nl', 'pl', 'sk', 'es'],
    ES: ['es'],
    FR: ['fr'],
    LU: ['fr', 'de'],
    NL: ['nl'],
    PL: ['pl'],
    SK: ['sk']
};

/**
 * Country code of the Home Planner application instance.
 * Home Planner currently runs a single DE instance.
 */
export const INSTANCE_COUNTRY_CODE = 'DE';

/** Name of the query param used to pre-select the application language. */
export const LANG_QUERY_PARAM = 'lang';

/**
 * Returns the languages configured for the given instance country.
 * Falls back to the default instance country when the country is unknown.
 */
export function getSupportedLanguages(countryCode: string = INSTANCE_COUNTRY_CODE): string[] {
    return COUNTRY_LANGUAGES[countryCode] ?? COUNTRY_LANGUAGES[INSTANCE_COUNTRY_CODE];
}

/**
 * Returns the default (main) language of the given instance country.
 */
export function getDefaultLanguage(countryCode: string = INSTANCE_COUNTRY_CODE): string {
    return getSupportedLanguages(countryCode)[0];
}

/**
 * Resolves the application language from a URL query string.
 *
 * When the `lang` query param matches one of the instance's configured
 * languages it is used, otherwise the instance default language is returned.
 *
 * @param search The URL query string (e.g. `window.location.search`).
 * @param countryCode The instance country code used to determine the configured languages.
 */
export function resolveLanguage(search: string = '', countryCode: string = INSTANCE_COUNTRY_CODE): string {
    const supportedLanguages = getSupportedLanguages(countryCode);
    const requestedLanguage = new URLSearchParams(search).get(LANG_QUERY_PARAM)?.toLowerCase();

    if (requestedLanguage && supportedLanguages.includes(requestedLanguage)) {
        return requestedLanguage;
    }

    return getDefaultLanguage(countryCode);
}
